import React, { PropsWithChildren, ReactNode, RefObject, createContext, useState } from "react";

/**
 * Hides UI and shows a spinner
 * @param props
 * @returns
 */
export const ModalContext = createContext<ModalContext>({} as ModalContext);
ModalContext.displayName = "ModalContext";

export interface ModalProps {
	closeOnBackgroundClick?: boolean;
	onClosed?: () => void;
	children?: React.ReactNode;
	show?: boolean;
	noPortal?: boolean;
	overflow?: string;
}

export interface ModalContext {
	alert: (message: ReactNode | string, title?: string) => any;
	popup: (body: ReactNode, title?: string, closeOnBackgroundClick?: boolean) => any;
	prompt: (
		message: ReactNode,
		title?: string,
		defaultValue?: string,
		confirmButtonTitle?: string,
		cancelButtonTitle?: string
	) => Promise<any>;
	confirm: (
		message: ReactNode,
		title?: string,
		confirmButtonTitle?: string,
		cancelButtonTitle?: string,
		extraFooter?: any,
		overflow?: string
	) => Promise<boolean>;
	hide: () => void;
}

export const ModalProvider: React.FunctionComponent<PropsWithChildren> = ({ children }) => {
	const [props, setProps] = useState<ModalProps>();
	const [hidden, setHidden] = useState<boolean>(true);
	const [modalChildren, setModalChildren] = useState<ReactNode>();
	const handleBackgroundClick = () => {
		if (props?.closeOnBackgroundClick) {
			hide();
			if (props?.onClosed) {
				props.onClosed();
			}
		}
	};

	const hide = () => {
		setHidden(true);
		setModalChildren(undefined);
	};

	const _showComplex = (title: string | undefined, body: ReactNode, footer: ReactNode, props?: ModalProps) => {
		show(
			<div
				className="modal-card responsive-modal-card"
				style={{ 
					overflow: props && props.overflow ? props.overflow : "hidden", 
					minWidth: "min(95vw, 320px)", 
					maxWidth: "min(95vw, 600px)",
					width: "auto"
				}}
			>
				<div className="responsive-modal-scrollable">
					<header className="modal-card-head responsive-modal-header">{title ? <p className="modal-card-title">{title}</p> : null}</header>
					<section
						className="modal-card-body responsive-modal-body"
						style={{ color: "#000000", overflow: props && props.overflow ? props.overflow : "auto" }}
					>
						{body}
					</section>
				</div>
				<footer className="modal-card-foot responsive-modal-footer">{footer}</footer>
			</div>,
			props
		);
	};

	const show = (children: ReactNode, props?: ModalProps) => {
		setModalChildren(children);
		setHidden(false);
		setProps(props);
	};

	const alert = (message: ReactNode | string, title?: string) => {
		return new Promise<void>((resolve) => {
			_showComplex(
				title,
				message,
				<button
					className="button is-primary"
					onClick={() => {
						hide();
						resolve();
					}}
				>
					OK
				</button>
			);
		});
	};

	const popup = (body: ReactNode, title?: string, closeOnBackgroundClick: boolean = true) => {
		return new Promise<void>((resolve) => {
			_showComplex(title, body, null, {
				closeOnBackgroundClick: closeOnBackgroundClick,
				onClosed: () => resolve()
			});
		});
	};

	const prompt = async (
		message: ReactNode,
		title: string = "",
		defaultValue: string = "",
		confirmButtonTitle: string = "OK",
		cancelButtonTitle: string = "Cancel"
	): Promise<any> => {
		let inputRef: RefObject<HTMLInputElement> = React.createRef();

		return new Promise<any>((resolve) => {
			_showComplex(
				title,
				<div className="field">
					<label className="label">{message}</label>
					<div className="control">
						<input className="input" type="text" ref={inputRef} autoFocus defaultValue={defaultValue} />
					</div>
				</div>,
				<React.Fragment>
					{!confirmButtonTitle ? null : (
						<button
							className="button is-success"
							onClick={() => {
								let value = inputRef.current?.value;
								hide();
								resolve(value);
							}}
						>
							{confirmButtonTitle}
						</button>
					)}
					{!cancelButtonTitle ? null : (
						<button
							className="button"
							onClick={() => {
								hide();
								resolve(false);
							}}
						>
							{cancelButtonTitle}
						</button>
					)}
				</React.Fragment>
			);
		});
	};

	const confirm = async (
		message: ReactNode,
		title: string = "",
		confirmButtonTitle: string = "OK",
		cancelButtonTitle: string = "Cancel",
		extraFooter: any = null,
		overflow?: string
	): Promise<any> => {
		return new Promise<any>((resolve) => {
			_showComplex(
				title,
				message,
				<React.Fragment>
					{!confirmButtonTitle ? null : (
						<button
							className="button is-success"
							onClick={() => {
								resolve(true);
								hide();
							}}
						>
							{confirmButtonTitle}
						</button>
					)}
					{!cancelButtonTitle ? null : (
						<button
							className="button"
							onClick={() => {
								resolve(false);
								hide();
							}}
						>
							{cancelButtonTitle}
						</button>
					)}
					{extraFooter}
				</React.Fragment>,
				{ overflow: overflow }
			);
		});
	};
	const value: ModalContext = {
		prompt,
		alert,
		confirm,
		popup,
		hide
	};
	return (
		<ModalContext.Provider value={value}>
			{!hidden ? (
				<div className="modal is-active" style={{ zIndex: 1000 }}>
					<div className="modal-background" onClick={handleBackgroundClick} />
					{modalChildren}
				</div>
			) : null}
			{children}
		</ModalContext.Provider>
	);
};
